import { computed, ref } from 'vue';
import { switchF7Css } from '../utils/cssSwitcher';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { useAuthStore } from '@/stores/authStore.js';
// Shared state that will be available to all components
const isArabic = ref(true);
export const lang = ref('ar');
const availableLang = ['ar', 'en'];

/**
 * Fire-and-forget PATCH to sync the user's preferred locale to the backend
 * (contract Section 8 / P5). The backend uses preferred_locale to author push
 * `headings`/`contents` in the right language. Best-effort:
 *   - PWA-only-aware: silently no-ops when there's no auth (login screen
 *     locale swap), so logged-out users can freely toggle language without
 *     a 401 firing the global auth:unauthorized handler.
 *   - Uses the single shared `jsonApiClient` (single axios invariant).
 *   - 401 → flows through the existing global interceptor (no parallel path).
 *   - All other errors → console.warn + continue (UI must not block on this).
 */
function syncPreferredLocaleToBackend(locale) {
  try {
    const authStore = useAuthStore();
    const userId = authStore.user?.id;
    if (!userId) return; // Logged out — no profile to PATCH.
    // jsonApiClient injects Content-Type/Accept application/vnd.api+json.
    jsonApiClient
      .patch(`/users/${userId}/-actions/profile`, {
        data: {
          type: 'users',
          attributes: { preferred_locale: locale },
        },
      })
      .catch((err) => {
        // 401 is caught by the global auth:unauthorized interceptor — do not
        // add a parallel path. Other errors are non-fatal (push will fall back
        // to the user's stored backend locale until the next switch retry).
        const status = err?.response?.status;
        if (status !== 401) {
          console.warn('[useLanguage] preferred_locale PATCH failed:', status, err?.message);
        }
      });
  } catch (err) {
    // useAuthStore() can throw if Pinia isn't initialized yet — safe to swallow,
    // language-toggle never blocks on this.
    console.warn('[useLanguage] preferred_locale sync skipped:', err);
  }
}

export function useLanguage() {
  // Initialize from localStorage if available
  const initLanguage = () => {
    const storedLang = localStorage.getItem('poolit-language');
    if (storedLang && availableLang.includes(storedLang)) {
      lang.value = storedLang;
    }


    isArabic.value = lang.value === 'ar';
    if (!storedLang) {
      localStorage.setItem('poolit-language', lang.value);
    }
    console.log('init lang', lang.value);
    isArabic.value = true;
    switchF7Css(isArabic.value);

  };

  const currentLang = computed(() => lang.value);
  // Toggle language
  const toggleLanguage = () => {
    isArabic.value = !isArabic.value;
    const next = isArabic.value ? 'ar' : 'en';
    lang.value = next;
    localStorage.setItem('poolit-language', next);
    // Sync to backend for push localization (contract Section 8). Fire-and-forget.
    syncPreferredLocaleToBackend(next);
  };

  return {
    isArabic,
    toggleLanguage,
    initLanguage,
    currentLang,
  };
}
